import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import type { Cup } from "../types/models";
import { Button } from "./Button";
import { InputField, SelectField } from "./Field";
import { Modal } from "./Modal";
import { useI18n } from "../i18n/I18nProvider";

type CupEditorProps = {
  open: boolean;
  cup?: Cup | null;
  onClose: () => void;
  onSave: (values: { name: string; volumeMl: number; icon: string }) => Promise<void>;
  onDelete?: (cup: Cup) => Promise<void>;
};

export function CupEditor({ open, cup, onClose, onSave, onDelete }: CupEditorProps) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [volumeMl, setVolumeMl] = useState(250);
  const [icon, setIcon] = useState("cup");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setName(cup?.name ?? "");
      setVolumeMl(cup?.volume_ml ?? 250);
      setIcon(cup?.icon ?? "cup");
    }
  }, [cup, open]);

  return (
    <Modal
      open={open}
      title={cup ? t("today.editCup") : t("today.addCup")}
      onClose={onClose}
      footer={
        <>
          {cup && onDelete ? (
            <Button
              variant="danger"
              icon={<Trash2 size={16} aria-hidden="true" />}
              disabled={pending}
              onClick={() => {
                setPending(true);
                void onDelete(cup).finally(() => setPending(false));
              }}
            >
              {t("common.delete")}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={pending || !name.trim() || volumeMl < 1}
            onClick={() => {
              setPending(true);
              void onSave({ name: name.trim(), volumeMl, icon }).finally(() => setPending(false));
            }}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <InputField label={t("today.cupName")} value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        <InputField
          label={t("today.cupVolume")}
          type="number"
          min={1}
          value={volumeMl}
          onChange={(event) => setVolumeMl(Number(event.target.value))}
        />
        <SelectField
          label={t("today.cupIcon")}
          value={icon}
          onChange={(event) => setIcon(event.target.value)}
          options={[
            { label: "Cup", value: "cup" },
            { label: "Bottle", value: "bottle" }
          ]}
        />
      </div>
    </Modal>
  );
}
